import React, { useEffect, useMemo, useState } from 'react';
import { makeStyles } from '@material-ui/core';
import AdVariantGrid, { GridCell } from './AdVariantGrid';
import { AD } from './adsAgentTokens';
import { AdTemplate } from './adTemplateIdeas';

// How many examples the page shows (one row on wide screens).
const EXAMPLE_LIMIT = 4;

const useStyles = makeStyles(() => ({
  root: {
    fontFamily: AD.fontFamily,
  },
  intro: {
    margin: '0 0 16px',
  },
  introTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    margin: 0,
    fontSize: 18,
    fontWeight: 700,
    color: AD.text,
  },
  introBadge: {
    padding: '3px 10px',
    borderRadius: AD.radiusPill,
    background: AD.primarySoft,
    color: AD.primary,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.4px',
    textTransform: 'uppercase',
  },
  introSub: {
    margin: '4px 0 0',
    fontSize: 13,
    color: AD.textMuted,
    lineHeight: 1.5,
  },
}));

const TemplateIdeasGallery = (props: Props): React.ReactElement | null => {
  const { templates } = props;
  const classes = useStyles();
  const [broken, setBroken] = useState<Set<string>>(new Set());

  // A sample whose image no longer loads is dropped rather than shown empty.
  useEffect(() => {
    let alive = true;
    templates.forEach((template) =>
      template.variants.forEach((variant) => {
        const probe = new Image();
        probe.onerror = () => {
          if (alive) {
            setBroken((current) => new Set(current).add(variant._id));
          }
        };
        probe.src = variant.imageUrl;
      }),
    );
    return () => {
      alive = false;
    };
  }, [templates]);

  const cells: GridCell[] = useMemo(
    () =>
      templates
        .flatMap((template) =>
          template.variants
            .filter((variant) => variant.imageUrl && !broken.has(variant._id))
            .map((variant) => ({
              key: `${template._id}:${variant.variantIndex}`,
              kind: 'variant' as const,
              variant,
            })),
        )
        .slice(0, EXAMPLE_LIMIT),
    [templates, broken],
  );

  // Nothing to show when no example image loads.
  if (!cells.length) {
    return null;
  }

  return (
    <section className={classes.root} aria-label="Example ads">
      <div className={classes.intro}>
        <h2 className={classes.introTitle}>
          Example ads made with AiSensy Ads Agent
          <span className={classes.introBadge}>Examples</span>
        </h2>
        <p className={classes.introSub}>
          These are sample ads to give you ideas. Describe your business above to
          generate your own, or sign up to create and run ads like these.
        </p>
      </div>
      <AdVariantGrid cells={cells} />
    </section>
  );
};

interface Props {
  templates: AdTemplate[];
}

export default TemplateIdeasGallery;
